import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MARKET } from '@/common/constants/markets';
import { ROUTES } from '@/common/constants/routes';
import { removeMemberFromSimulation } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/InsuranceAddMembersPage.utils';
import { TAddFamilyMembersFormValues } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/AddMemberCardForm/AddMemberCardForm.schema';
import { ConfirmDetailsStep } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/ConfirmDetaisStep/ConfirmDetailsStep';
import { FamilyMembersInfoStep } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/FamilyMembersInfoStep/FamilyMembersInfoStep';
import { PaymentMethodStep } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/PaymentMethodStep/PaymentMethodStep';
import { useInsuranceContext } from '@/common/contexts/insurance/InsuranceContext';
import { useGetInsurancePolicy } from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import { TMenhirInsurancePaymentMethod } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { getInsuranceFamilyPaymentType } from '@/common/integrations/menhir/employee/insurance/insurance.utils';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useMarket } from '@/web/hooks/useMarket';
import { Redirect, useLocalSearchParams } from 'expo-router';

export const InsuranceAddMembersPage = () => {
  const { t } = useTranslation();
  const params = useLocalSearchParams<{ policyId: string }>();
  const { slug } = useMarket();
  const { simulation, setSimulation } = useInsuranceContext();
  const [paymentMethod, setPaymentMethod] = useState<
    TMenhirInsurancePaymentMethod | undefined
  >();

  const { policy } = useGetInsurancePolicy(params.policyId);
  const companyPaysAll = policy
    ? getInsuranceFamilyPaymentType(policy) === 'pay_none'
    : false;

  if (!params.policyId || simulation.persons.length === 0) {
    return <Redirect href={ROUTES.INSURANCE} />;
  }

  const onRemoveMember = (
    member: TAddFamilyMembersFormValues['members'][number]
  ) => {
    const updatedSimulation = removeMemberFromSimulation(simulation, member);
    setSimulation({
      ...simulation,
      totalCost: updatedSimulation.totalCost,
      persons: updatedSimulation.persons,
    });
  };

  const showPaymentMethodStep =
    !companyPaysAll && simulation && slug !== MARKET.ES;

  return (
    <FlowLayout.Root
      headerProps={{ title: t('dashboard.insurance.add_family_members.title') }}
    >
      <FlowLayout.Step
        title={t('dashboard.insurance_detail.add_family_members_step1.title')}
      >
        <FamilyMembersInfoStep
          persons={simulation.persons}
          policyId={params.policyId as string}
          onRemove={onRemoveMember}
        />
      </FlowLayout.Step>
      {showPaymentMethodStep && (
        <FlowLayout.Step
          title={t('dashboard.insurance_detail.add_family_members_step2.title')}
        >
          <PaymentMethodStep
            simulation={simulation}
            setPaymentMethod={setPaymentMethod}
            paymentMethod={paymentMethod}
          />
        </FlowLayout.Step>
      )}
      <FlowLayout.Step
        title={t('dashboard.insurance_detail.add_family_members_step3.title')}
      >
        <ConfirmDetailsStep
          paymentMethod={paymentMethod}
          companyPaysAll={companyPaysAll}
        />
      </FlowLayout.Step>
    </FlowLayout.Root>
  );
};
